import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './OpsSecurityScanning.css';

const API_BASE = process.env.NODE_ENV === 'development' 
    ? `${window.location.protocol}//${window.location.hostname}:5001/api` 
    : '/api';

// Date Formatter: YYYY-month -DD (e.g. "2026-july -20")
const getFormattedDate = (dateObj = new Date()) => {
    const year = dateObj.getFullYear();
    const months = [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ];
    const month = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    return `${year}-${month} -${day}`;
};

// Web Audio API beep helper for physical scanner feedback
const playBeep = (type) => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        
        if (ctx.state === 'suspended') {
            ctx.resume();
        }

        if (type === 'success') {
            // High-pitched pleasant success beep (800Hz, 120ms)
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(800, ctx.currentTime);
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.12);
        } else if (type === 'duplicate') {
            // Low-pitched warning sawtooth buzz (220Hz and 180Hz, longer duration)
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sawtooth';
            osc1.frequency.setValueAtTime(220, ctx.currentTime);
            gain1.gain.setValueAtTime(0.12, ctx.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start();
            osc1.stop(ctx.currentTime + 0.25);

            // Secondary deeper warning tone after 120ms
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sawtooth';
            osc2.frequency.setValueAtTime(170, ctx.currentTime + 0.12);
            gain2.gain.setValueAtTime(0.12, ctx.currentTime + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.38);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(ctx.currentTime + 0.12);
            osc2.stop(ctx.currentTime + 0.38);
        }
    } catch (err) {
        console.warn('[Audio feedback] Play beep failure:', err);
    }
};

const LOCATION_OPTIONS = ['PKSLK', 'PKLHE', 'PKKHR'];

const OpsSecurityScanning = ({ onBack, currentUser, isSuperUser, companySlug }) => {
    const [dates, setDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getFormattedDate());
    const [scans, setScans] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [trackingInput, setTrackingInput] = useState('');
    const [lastScanResult, setLastScanResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [datesLoading, setDatesLoading] = useState(false);
    const [scansLoading, setScansLoading] = useState(false);

    // Location state & Modal
    const [selectedLocation, setSelectedLocation] = useState('PKSLK');
    const [tempLocation, setTempLocation] = useState('PKSLK');
    const [showLocationModal, setShowLocationModal] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Export Modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFromDate, setExportFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [exportToDate, setExportToDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [exportLocation, setExportLocation] = useState('ALL');
    const [exporting, setExporting] = useState(false);

    const inputRef = useRef(null);
    const queueRef = useRef([]);
    const isProcessingQueueRef = useRef(false);
    const locationRef = useRef(selectedLocation);

    // Keep locationRef in sync
    useEffect(() => {
        locationRef.current = selectedLocation;
    }, [selectedLocation]);

    // Fetch dates listed on the left side
    const fetchDates = useCallback(async () => {
        setDatesLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/ops-scanning/dates`);
            if (res.data.success) {
                setDates(res.data.dates);
            }
        } catch (err) {
            console.error('[OPS Scanning] Fetching dates failed:', err);
        } finally {
            setDatesLoading(false);
        }
    }, []);

    // Fetch scans for the selected date
    const fetchScansForDate = useCallback(async (date) => {
        setScansLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/ops-scanning/scans?date=${encodeURIComponent(date)}`);
            if (res.data.success) {
                setScans(res.data.scans);
            }
        } catch (err) {
            console.error('[OPS Scanning] Fetching scans failed:', err);
        } finally {
            setScansLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchDates();
    }, [fetchDates]);

    // Load scans when selected date changes
    useEffect(() => {
        if (selectedDate) {
            fetchScansForDate(selectedDate);
        }
    }, [selectedDate, fetchScansForDate]);

    // Keep input field focused while scanning
    useEffect(() => {
        if (isScanning && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isScanning]);

    // Focus input on any click within active scanner console
    useEffect(() => {
        if (!isScanning) return;

        const handleWindowClick = () => {
            if (inputRef.current && document.activeElement !== inputRef.current && !showLocationModal && !showExportModal) {
                inputRef.current.focus();
            }
        };

        window.addEventListener('click', handleWindowClick);
        return () => window.removeEventListener('click', handleWindowClick);
    }, [isScanning, showLocationModal, showExportModal]);

    // Click "Start Scanning" -> Prompt Location Modal
    const handleStartScanningClick = () => {
        setTempLocation(selectedLocation || 'PKSLK');
        setShowLocationModal(true);
    };

    const confirmStartScanning = () => {
        setSelectedLocation(tempLocation);
        locationRef.current = tempLocation;
        setShowLocationModal(false);
        const today = getFormattedDate();
        setSelectedDate(today);
        setIsScanning(true);
        setLastScanResult(null);
    };

    const stopScanMode = () => {
        setIsScanning(false);
        fetchDates();
    };

    // Queue Processor for fast barcode scans
    const processQueue = async () => {
        if (isProcessingQueueRef.current) return;
        isProcessingQueueRef.current = true;
        setLoading(true);

        while (queueRef.current.length > 0) {
            const barcode = queueRef.current.shift();
            const scanDate = getFormattedDate();
            const currentLocation = locationRef.current;

            try {
                const res = await axios.post(`${API_BASE}/ops-scanning/scan`, {
                    trackingNumber: barcode,
                    date: scanDate,
                    location: currentLocation
                });

                if (res.data.success) {
                    const { isDuplicate, newScan, priorScans, priorDates } = res.data;

                    if (isDuplicate) {
                        playBeep('duplicate');
                    } else {
                        playBeep('success');
                    }

                    setLastScanResult({
                        trackingNumber: barcode,
                        isDuplicate,
                        timestamp: newScan.timestamp,
                        scannedBy: newScan.scannedBy,
                        location: newScan.location,
                        priorScans: priorScans || [],
                        priorDates: priorDates || []
                    });

                    setScans(prev => [newScan, ...prev]);

                    setDates(prev => {
                        if (prev.includes(scanDate)) return prev;
                        return [scanDate, ...prev];
                    });
                }
            } catch (err) {
                console.error('[OPS Scanning] Error scanning barcode:', err);
                alert(err.response?.data?.message || `Error scanning ${barcode}`);
            }
        }

        isProcessingQueueRef.current = false;
        setLoading(false);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Submitting barcode scans (Clear input INSTANTLY synchronously to prevent concatenation!)
    const handleScanSubmit = (e) => {
        if (e) e.preventDefault();
        
        const rawValue = trackingInput || (inputRef.current ? inputRef.current.value : '');
        
        // INSTANT SYNCHRONOUS CLEAR to avoid fast scanner concatenation
        setTrackingInput('');
        if (inputRef.current) {
            inputRef.current.value = '';
        }

        if (!rawValue.trim()) return;

        // Split by newlines, spaces, tabs, commas to handle concatenated/multi-line scans
        const tokens = rawValue.split(/[\s,\r\n]+/).map(t => t.trim()).filter(Boolean);
        if (tokens.length === 0) return;

        queueRef.current.push(...tokens);
        processQueue();
    };

    const handleBlur = () => {
        if (isScanning && inputRef.current && !showLocationModal && !showExportModal) {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 10);
        }
    };

    const handleDeleteScan = async (scanId) => {
        if (!window.confirm("Are you sure you want to delete this scan record?")) return;
        try {
            const res = await axios.delete(`${API_BASE}/ops-scanning/scan/${scanId}`);
            if (res.data.success) {
                setScans(prev => prev.filter(s => s._id !== scanId));
                if (lastScanResult) {
                    const deletedScan = scans.find(s => s._id === scanId);
                    if (deletedScan && deletedScan.trackingNumber === lastScanResult.trackingNumber) {
                        setLastScanResult(null);
                    }
                }
            }
        } catch (err) {
            console.error('[OPS Scanning] Error deleting scan:', err);
            alert(err.response?.data?.message || 'Error deleting scan. Please try again.');
        } finally {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    // Filter scans based on search term
    const filteredScans = scans.filter(s => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase().trim();
        return (
            (s.trackingNumber || '').toLowerCase().includes(term) ||
            (s.location || '').toLowerCase().includes(term) ||
            (s.scannedBy || '').toLowerCase().includes(term)
        );
    });

    // Handle Excel Download
    const handleDownloadExcel = async () => {
        if (!exportFromDate || !exportToDate) {
            alert('Please select both From Date and To Date');
            return;
        }

        setExporting(true);
        try {
            const res = await axios.get(
                `${API_BASE}/ops-scanning/export?startDate=${exportFromDate}&endDate=${exportToDate}&location=${exportLocation}`
            );

            if (res.data.success && res.data.scans) {
                const exportScans = res.data.scans;
                if (exportScans.length === 0) {
                    alert('No scan records found for the selected date range and location filter.');
                    setExporting(false);
                    return;
                }

                // Format data for Excel sheet
                const excelData = exportScans.map((s, idx) => {
                    const isDup = s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || exportScans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1;
                    return {
                        'S.No': idx + 1,
                        'Date': s.date || '',
                        'Time': s.timestamp ? new Date(s.timestamp).toLocaleTimeString() : '',
                        'Location': s.location || 'N/A',
                        'Tracking Number / Barcode': s.trackingNumber || '',
                        'Scanned By': s.scannedBy || '',
                        'Scan Status': isDup ? 'Duplicate' : 'Original',
                        'Other Scan Dates': (s.priorDates || []).join(', ')
                    };
                });

                const worksheet = XLSX.utils.json_to_sheet(excelData);

                // Set column widths
                worksheet['!cols'] = [
                    { wch: 8 },  // S.No
                    { wch: 18 }, // Date
                    { wch: 14 }, // Time
                    { wch: 12 }, // Location
                    { wch: 28 }, // Tracking Number
                    { wch: 25 }, // Scanned By
                    { wch: 14 }, // Status
                    { wch: 30 }  // Other Scan Dates
                ];

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'OPS Scans');

                const fileName = `OPS_Scanning_Report_${exportFromDate}_to_${exportToDate}.xlsx`;
                XLSX.writeFile(workbook, fileName);

                setShowExportModal(false);
            }
        } catch (err) {
            console.error('[OPS Scanning] Download excel failed:', err);
            alert(err.response?.data?.message || 'Error exporting scans to Excel.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="ops-scanning-container">
            {/* Left Side Dates Sidebar */}
            <div className="ops-sidebar">
                <div className="ops-sidebar-title">
                    <span>📅</span> Scan Archives
                </div>
                
                {datesLoading && <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>Loading dates...</div>}
                
                {!datesLoading && dates.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: '1rem' }}>
                        No scans recorded yet.
                    </div>
                )}

                <ul className="ops-date-list">
                    {dates.map(date => (
                        <li 
                            key={date} 
                            className={`ops-date-item ${selectedDate === date ? 'active' : ''}`}
                            onClick={() => {
                                if (!isScanning) {
                                    setSelectedDate(date);
                                    setLastScanResult(null);
                                }
                            }}
                            style={{ cursor: isScanning ? 'not-allowed' : 'pointer', opacity: isScanning && selectedDate !== date ? 0.5 : 1 }}
                        >
                            <span>{date}</span>
                            <span>⚡</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Right Side Main Area */}
            <div className="ops-main">
                {/* Header Section */}
                <div className="ops-header">
                    <div className="ops-header-info">
                        <h2>🛡️ OPS Security Scanning</h2>
                        <p>Real-time package validation & barcode duplicate tracking</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button className="ops-btn excel" onClick={() => setShowExportModal(true)}>
                            📥 Download Excel
                        </button>
                        {!isScanning ? (
                            <button className="ops-btn primary" onClick={handleStartScanningClick}>
                                🚀 Start Scanning
                            </button>
                        ) : (
                            <button className="ops-btn danger" onClick={stopScanMode}>
                                🛑 Stop Scanning
                            </button>
                        )}
                        <button className="ops-btn secondary" onClick={onBack} disabled={isScanning}>
                            ↩ Back
                        </button>
                    </div>
                </div>

                {/* Live Scanner Console */}
                {isScanning && (
                    <div className="ops-scanner-console">
                        <div className="ops-status-bar">
                            <div className="ops-status-badge">
                                <span className="dot"></span>
                                Live Scanner Active ({selectedDate})
                            </div>
                            <div className="ops-location-indicator">
                                📍 Active Location: <strong>{selectedLocation}</strong>
                                <button className="ops-change-loc-btn" onClick={handleStartScanningClick}>
                                    🔄 Switch Location
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleScanSubmit} className="ops-input-wrapper">
                            <span className="ops-barcode-icon">🔍</span>
                            <input
                                ref={inputRef}
                                type="text"
                                className="ops-barcode-input"
                                placeholder="Scan or type barcode here (Fast scan supported)..."
                                value={trackingInput}
                                onChange={e => setTrackingInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        handleScanSubmit(e);
                                    }
                                }}
                                onBlur={handleBlur}
                                autoComplete="off"
                            />
                        </form>

                        {loading && <div style={{ color: '#38bdf8', fontWeight: 600, marginTop: '6px' }}>⚡ Processing barcode scan...</div>}

                        {/* Last Scan Status display */}
                        {lastScanResult && (
                            <div className={`ops-alert ${lastScanResult.isDuplicate ? 'duplicate' : 'success'}`}>
                                <div className="ops-alert-title">
                                    {lastScanResult.isDuplicate ? '⚠️ Duplicate Scan Detected!' : '✅ Scanned Successfully!'}
                                </div>
                                <div className="ops-alert-body">
                                    <strong>Tracking Number:</strong> <code style={{ fontSize: '1.25rem', display: 'block', margin: '4px 0', fontFamily: 'monospace' }}>{lastScanResult.trackingNumber}</code>
                                    <div>Location: <strong>{lastScanResult.location || selectedLocation}</strong> | Scanned at: {new Date(lastScanResult.timestamp).toLocaleTimeString()} by {lastScanResult.scannedBy}</div>
                                    
                                    {lastScanResult.isDuplicate && (
                                        <div className="ops-prior-scans">
                                            <h4>
                                                ⚠️ Duplicate Detected ({lastScanResult.priorScans?.length || 0} prior scan{lastScanResult.priorScans?.length === 1 ? '' : 's'}):
                                            </h4>
                                            {lastScanResult.priorDates && lastScanResult.priorDates.length > 0 && (
                                                <div style={{ color: '#fef08a', fontSize: '0.88rem', marginBottom: '6px', fontWeight: 600 }}>
                                                    📅 Exists in Date(s): {lastScanResult.priorDates.join(', ')}
                                                </div>
                                            )}
                                            <ul className="ops-prior-list">
                                                {lastScanResult.priorScans?.map((s, idx) => (
                                                    <li key={idx}>
                                                        {new Date(s.timestamp).toLocaleString()} (Date: {s.date}, Location: {s.location || 'N/A'}) - by {s.scannedBy}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Scans List for selected date */}
                <div className="ops-table-container">
                    <div className="ops-table-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h3>Scans for {selectedDate}</h3>
                            <span className="count-badge">
                                Total: {filteredScans.length} | Originals: {filteredScans.filter(s => !(s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || scans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1)).length} | Duplicates: {filteredScans.filter(s => s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || scans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1).length}
                            </span>
                        </div>

                        {/* Live Search Input */}
                        <div className="ops-search-box">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="ops-search-input"
                                placeholder="Search tracking number, location, user..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button className="ops-clear-search" onClick={() => setSearchTerm('')}>✕</button>
                            )}
                        </div>
                    </div>

                    {scansLoading ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Loading scans...</div>
                    ) : filteredScans.length === 0 ? (
                        <div className="no-scans-placeholder">
                            <span className="icon">📦</span>
                            {searchTerm ? 'No barcodes match your search query.' : 'No barcodes scanned on this date yet. Start scanning to begin record.'}
                        </div>
                    ) : (
                        <table className="ops-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Location</th>
                                    <th>Tracking Number / Barcode</th>
                                    <th>Scanned By</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredScans.map((scan, idx) => {
                                    const isDuplicate = scan.isDuplicate || (scan.priorDates && scan.priorDates.length > 0) || scans.filter(s => s.trackingNumber?.trim().toUpperCase() === scan.trackingNumber?.trim().toUpperCase()).length > 1;
                                    return (
                                        <tr key={scan._id || idx}>
                                            <td>{new Date(scan.timestamp).toLocaleTimeString()}</td>
                                            <td>
                                                <span className="ops-loc-badge">
                                                    {scan.location || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{scan.trackingNumber}</td>
                                            <td>{scan.scannedBy}</td>
                                            <td>
                                                {isDuplicate ? (
                                                    <span 
                                                        className="ops-badge duplicate"
                                                        title={scan.priorDates && scan.priorDates.length > 0 ? `Also scanned on: ${scan.priorDates.join(', ')}` : 'Duplicate scan'}
                                                    >
                                                        ⚠️ Duplicate
                                                        {scan.priorDates && scan.priorDates.length > 0 && (
                                                            <span style={{ fontSize: '0.72rem', display: 'block', opacity: 0.9, marginTop: '2px' }}>
                                                                📅 {scan.priorDates.slice(0, 2).join(', ')}{scan.priorDates.length > 2 ? '...' : ''}
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="ops-badge original">Original</span>
                                                )}
                                            </td>
                                            <td>
                                                <button 
                                                    className="ops-btn-delete"
                                                    onClick={() => handleDeleteScan(scan._id)}
                                                    title="Delete Scan"
                                                    style={{
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 'bold',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Location Prompt Modal */}
            {showLocationModal && (
                <div className="ops-modal-overlay">
                    <div className="ops-modal-content">
                        <h3>📍 Select Scanning Location</h3>
                        <p>Choose your current operational facility before scanning:</p>

                        <div className="ops-location-grid">
                            {LOCATION_OPTIONS.map(loc => (
                                <button
                                    key={loc}
                                    type="button"
                                    className={`ops-location-option ${tempLocation === loc ? 'active' : ''}`}
                                    onClick={() => setTempLocation(loc)}
                                >
                                    <span className="loc-icon">🏬</span>
                                    <span className="loc-name">{loc}</span>
                                </button>
                            ))}
                        </div>

                        <div className="ops-modal-actions">
                            <button
                                className="ops-btn secondary"
                                onClick={() => setShowLocationModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="ops-btn primary"
                                onClick={confirmStartScanning}
                            >
                                ✅ Confirm & Start Scanning
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Download Excel Range Modal */}
            {showExportModal && (
                <div className="ops-modal-overlay">
                    <div className="ops-modal-content">
                        <h3>📊 Download OPS Scans Excel</h3>
                        <p>Select date range and location filter to export scan history:</p>

                        <div className="ops-export-form">
                            <div className="ops-form-group">
                                <label>From Date:</label>
                                <input
                                    type="date"
                                    value={exportFromDate}
                                    onChange={e => setExportFromDate(e.target.value)}
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>To Date:</label>
                                <input
                                    type="date"
                                    value={exportToDate}
                                    onChange={e => setExportToDate(e.target.value)}
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Location Filter:</label>
                                <select
                                    value={exportLocation}
                                    onChange={e => setExportLocation(e.target.value)}
                                >
                                    <option value="ALL">All Locations</option>
                                    {LOCATION_OPTIONS.map(loc => (
                                        <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="ops-modal-actions">
                            <button
                                className="ops-btn secondary"
                                onClick={() => setShowExportModal(false)}
                                disabled={exporting}
                            >
                                Cancel
                            </button>
                            <button
                                className="ops-btn excel"
                                onClick={handleDownloadExcel}
                                disabled={exporting}
                            >
                                {exporting ? 'Generating Excel...' : '📥 Export to Excel (.xlsx)'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpsSecurityScanning;
